/**
 * Confetti component for celebrations
 * Renders animated confetti pieces falling from top
 */

import { useMemo, useEffect, useState, memo } from "react";
import styles from "./Confetti.module.css";

// =============================================================================
// Types
// =============================================================================

interface ConfettiProps {
  /** Whether confetti is active */
  active: boolean;
  /** Number of confetti pieces */
  count?: number;
  /** Duration in seconds before auto-stop */
  duration?: number;
  /** Custom colors */
  colors?: string[];
}

interface ConfettiPiece {
  id: number;
  x: number;
  delay: number;
  rotation: number;
  color: string;
  size: number;
  type: "square" | "circle" | "strip";
}

// =============================================================================
// Default Colors
// =============================================================================

const DEFAULT_COLORS = [
  "#f6e05e", // Yellow
  "#4fd1c5", // Teal
  "#ed64a6", // Pink
  "#667eea", // Purple
  "#48bb78", // Green
  "#fc8181", // Red
  "#63b3ed", // Blue
];

// =============================================================================
// Component
// =============================================================================

export const Confetti = memo(function Confetti({
  active,
  count = 80,
  duration = 5,
  colors = DEFAULT_COLORS,
}: ConfettiProps) {
  const [isVisible, setIsVisible] = useState(active);

  // Generate confetti pieces
  const pieces = useMemo<ConfettiPiece[]>(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 2,
      rotation: Math.random() * 360,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: 8 + Math.random() * 8,
      type: (["square", "circle", "strip"] as const)[Math.floor(Math.random() * 3)],
    }));
  }, [count, colors]);

  // Auto-hide after duration
  useEffect(() => {
    if (active) {
      setIsVisible(true);
      const timeout = setTimeout(() => {
        setIsVisible(false);
      }, duration * 1000);
      return () => clearTimeout(timeout);
    } else {
      setIsVisible(false);
    }
  }, [active, duration]);

  if (!isVisible) return null;

  return (
    <div className={styles.container} aria-hidden="true">
      {pieces.map((piece) => (
        <div
          key={piece.id}
          className={`${styles.piece} ${styles[piece.type]}`}
          style={{
            "--x": `${piece.x}vw`,
            "--delay": `${piece.delay}s`,
            "--rotation": `${piece.rotation}deg`,
            "--color": piece.color,
            "--size": `${piece.size}px`,
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
});

export default Confetti;
